import { useMemo, useState } from 'react';
import { CalendarClock, Plus, Repeat, Trash2, X } from 'lucide-react';
import { Button, Card, Input, Select } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import { useSavePaymentSchedule } from '@/hooks/useDeals';
import { formatCurrency } from '@/lib/utils';
import { cn } from '@/lib/utils';
import type { Deal, PaymentCadence, SavePaymentSchedulePayload } from '@/types';

/**
 * Proposed payment schedule editor.
 *
 * This is the input to the financials cash forecast: a deal that only carries a
 * flat value cannot say which half-month period its money lands in. Fixed-bid
 * deals get dated installments, retainers get an amount + cadence + start +
 * minimum term, and a deal may carry both.
 */

const cadenceOptions: { value: PaymentCadence; label: string; noun: string; months: number }[] = [
  { value: 'MONTHLY', label: 'Monthly', noun: 'month', months: 1 },
  { value: 'QUARTERLY', label: 'Quarterly', noun: 'quarter', months: 3 },
  { value: 'ANNUAL', label: 'Annually', noun: 'year', months: 12 },
];

interface InstallmentRow {
  key: string;
  dueDate: string;
  mode: 'AMOUNT' | 'PERCENT';
  value: string;
  label: string;
}

let rowSeq = 0;
const nextKey = () => `row-${rowSeq++}`;

function toNumber(value: string): number {
  const n = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function addMonths(dateStr: string, months: number): string {
  const base = new Date(`${dateStr.slice(0, 10)}T00:00:00.000Z`);
  if (Number.isNaN(base.getTime())) return today();
  const day = base.getUTCDate();
  const lastDay = new Date(
    Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + months + 1, 0),
  ).getUTCDate();
  return new Date(
    Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + months, Math.min(day, lastDay)),
  )
    .toISOString()
    .slice(0, 10);
}

function formatDay(dateStr: string): string {
  const d = new Date(`${dateStr.slice(0, 10)}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function PaymentScheduleCard({ deal }: { deal: Deal }) {
  const schedule = deal.paymentSchedule ?? null;
  const [editing, setEditing] = useState(false);

  return (
    <Card padding="md" className="mb-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="section-header">Payment Schedule</h3>
          <p className="text-xs text-grey-500 mt-1">
            When this deal's money is expected to arrive. Financials forecasts cash from it.
          </p>
        </div>
        {!editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            {schedule ? 'Edit' : <><Plus className="w-3.5 h-3.5" /> Add schedule</>}
          </Button>
        )}
      </div>

      {editing ? (
        <PaymentScheduleForm deal={deal} onDone={() => setEditing(false)} />
      ) : schedule ? (
        <PaymentScheduleSummaryView deal={deal} />
      ) : (
        <div className="rounded-lg border border-dashed border-grey-300 px-4 py-6 text-center">
          <CalendarClock className="w-6 h-6 text-grey-300 mx-auto mb-2" />
          <p className="text-body-sm text-grey-500">
            No payment schedule yet — financials can't forecast when this deal's cash lands.
          </p>
        </div>
      )}
    </Card>
  );
}

function PaymentScheduleSummaryView({ deal }: { deal: Deal }) {
  const schedule = deal.paymentSchedule!;
  const currency = schedule.currency || deal.currency || 'USD';
  const recurring = schedule.recurring;
  const cadence = cadenceOptions.find((c) => c.value === recurring?.cadence);

  return (
    <div className="space-y-4">
      {schedule.installments.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-grey-500 uppercase tracking-wider mb-2">
            Fixed installments
          </p>
          <div className="divide-y divide-grey-100 border border-grey-200 rounded-lg">
            {schedule.installments.map((inst) => (
              <div key={inst.id} className="flex items-center justify-between px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-grey-600 whitespace-nowrap">
                    {formatDay(inst.dueDate)}
                  </span>
                  {inst.label && (
                    <span className="text-sm text-grey-500 truncate">· {inst.label}</span>
                  )}
                  {inst.percentage && (
                    <Badge variant="outline" size="sm">{Number(inst.percentage)}%</Badge>
                  )}
                </div>
                <span className="text-sm font-semibold text-secondary whitespace-nowrap">
                  {formatCurrency(Number(inst.amount), currency)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {recurring && (
        <div>
          <p className="text-xs font-semibold text-grey-500 uppercase tracking-wider mb-2">
            Recurring
          </p>
          <div className="flex items-center gap-2 border border-grey-200 rounded-lg px-3 py-2">
            <Repeat className="w-4 h-4 text-grey-400 flex-shrink-0" />
            <span className="text-sm text-grey-600">
              <span className="font-semibold text-secondary">
                {formatCurrency(Number(recurring.amount), currency)}
              </span>{' '}
              per {cadence?.noun ?? 'period'} from {formatDay(recurring.startDate)} ·{' '}
              {recurring.minimumMonths}-month minimum (
              {formatCurrency(Number(recurring.minimumTotal), currency)} over{' '}
              {recurring.occurrences} charge{recurring.occurrences === 1 ? '' : 's'})
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-grey-100">
        <div className="flex items-center gap-4 text-sm pt-3">
          <span className="text-grey-500">
            Scheduled{' '}
            <span className="font-semibold text-secondary">
              {formatCurrency(Number(schedule.summary.scheduledTotal), currency)}
            </span>
          </span>
          {schedule.summary.dealValue && (
            <span className="text-grey-500">
              Deal value{' '}
              <span className="font-semibold text-secondary">
                {formatCurrency(Number(schedule.summary.dealValue), currency)}
              </span>
            </span>
          )}
          {schedule.summary.remainder && !schedule.summary.balanced && (
            <Badge variant="warning" size="sm">
              {Number(schedule.summary.remainder) > 0 ? 'Unscheduled' : 'Over'}{' '}
              {formatCurrency(Math.abs(Number(schedule.summary.remainder)), currency)}
            </Badge>
          )}
          {schedule.summary.balanced && (
            <Badge variant="success" size="sm">Balanced</Badge>
          )}
        </div>
        <span className="text-xs text-grey-400 pt-3">
          {schedule.projectedPayments.length} dated payment
          {schedule.projectedPayments.length === 1 ? '' : 's'} for forecasting
        </span>
      </div>
    </div>
  );
}

function PaymentScheduleForm({ deal, onDone }: { deal: Deal; onDone: () => void }) {
  const schedule = deal.paymentSchedule ?? null;
  const currency = deal.currency || 'USD';
  const dealValue = deal.value === undefined || deal.value === null ? null : Number(deal.value);
  const save = useSavePaymentSchedule();
  const [error, setError] = useState<string | null>(null);

  const [fixedOn, setFixedOn] = useState(!!schedule && schedule.installments.length > 0);
  const [recurringOn, setRecurringOn] = useState(!!schedule?.recurring);

  const [rows, setRows] = useState<InstallmentRow[]>(() =>
    (schedule?.installments ?? []).map((inst) => ({
      key: nextKey(),
      dueDate: inst.dueDate,
      mode: inst.percentage ? ('PERCENT' as const) : ('AMOUNT' as const),
      value: inst.percentage ? String(Number(inst.percentage)) : String(Number(inst.amount)),
      label: inst.label ?? '',
    })),
  );

  const [recurringAmount, setRecurringAmount] = useState(
    schedule?.recurring ? String(Number(schedule.recurring.amount)) : '',
  );
  const [cadence, setCadence] = useState<PaymentCadence>(schedule?.recurring?.cadence ?? 'MONTHLY');
  const [startDate, setStartDate] = useState(
    schedule?.recurring?.startDate ?? deal.dueDate?.slice(0, 10) ?? today(),
  );
  const [minimumMonths, setMinimumMonths] = useState(
    schedule?.recurring ? String(schedule.recurring.minimumMonths) : '12',
  );

  const rowAmount = (row: InstallmentRow): number =>
    row.mode === 'PERCENT'
      ? Math.round(((dealValue ?? 0) * toNumber(row.value)) / 100 * 100) / 100
      : toNumber(row.value);

  const fixedTotal = useMemo(
    () => (fixedOn ? rows.reduce((sum, r) => sum + rowAmount(r), 0) : 0),
    [rows, fixedOn, dealValue],
  );

  const cadenceMonths = cadenceOptions.find((c) => c.value === cadence)?.months ?? 1;
  const occurrences = Math.max(1, Math.ceil((Number(minimumMonths) || 1) / cadenceMonths));
  const recurringTotal = recurringOn ? toNumber(recurringAmount) * occurrences : 0;
  const scheduledTotal = Math.round((fixedTotal + recurringTotal) * 100) / 100;
  const remaining = dealValue === null ? null : Math.round((dealValue - scheduledTotal) * 100) / 100;

  const addRow = (preset?: { dueDate?: string; mode?: 'AMOUNT' | 'PERCENT'; value?: string }) => {
    setRows((prev) => {
      const last = prev[prev.length - 1];
      const defaultDate = last ? addMonths(last.dueDate, 1) : deal.dueDate?.slice(0, 10) ?? today();
      // Default the new row to whatever is still unaccounted for, so the common
      // case ("the rest on delivery") is one click.
      const outstanding =
        dealValue === null
          ? 0
          : Math.max(0, Math.round((dealValue - prev.reduce((s, r) => s + rowAmount(r), 0) - recurringTotal) * 100) / 100);
      return [
        ...prev,
        {
          key: nextKey(),
          dueDate: preset?.dueDate ?? defaultDate,
          mode: preset?.mode ?? 'AMOUNT',
          value: preset?.value ?? (outstanding > 0 ? String(outstanding) : ''),
          label: '',
        },
      ];
    });
  };

  const applyEvenSplit = (count: number) => {
    const base = deal.dueDate?.slice(0, 10) ?? today();
    const pct = Math.round((100 / count) * 100) / 100;
    setFixedOn(true);
    setRows(
      Array.from({ length: count }, (_, i) => ({
        key: nextKey(),
        dueDate: addMonths(base, i),
        mode: 'PERCENT' as const,
        // Put any rounding dust on the last row so percentages always total 100.
        value: String(i === count - 1 ? Math.round((100 - pct * (count - 1)) * 100) / 100 : pct),
        label: '',
      })),
    );
  };

  const handleSave = async () => {
    setError(null);

    const activeRows = fixedOn ? rows : [];
    for (const row of activeRows) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(row.dueDate)) {
        return setError('Every installment needs a date.');
      }
      if (row.value.trim() === '' || toNumber(row.value) < 0) {
        return setError('Every installment needs a positive amount or percentage.');
      }
      if (row.mode === 'PERCENT' && dealValue === null) {
        return setError('Set a deal value before using percentages, or enter flat amounts.');
      }
    }

    if (recurringOn) {
      if (toNumber(recurringAmount) <= 0) return setError('Recurring amount must be greater than zero.');
      if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return setError('Recurring needs a start date.');
      if (!Number(minimumMonths) || Number(minimumMonths) < 1) {
        return setError('Minimum months must be at least 1.');
      }
    }

    if (!activeRows.length && !recurringOn) {
      return setError('Add at least one installment or recurring terms — or remove the schedule.');
    }

    const payload: SavePaymentSchedulePayload = {
      installments: activeRows.map((row) => ({
        dueDate: row.dueDate,
        ...(row.mode === 'PERCENT'
          ? { percentage: toNumber(row.value) }
          : { amount: toNumber(row.value) }),
        ...(row.label.trim() ? { label: row.label.trim() } : {}),
      })),
      recurring: recurringOn
        ? {
            amount: toNumber(recurringAmount),
            cadence,
            startDate,
            minimumMonths: Number(minimumMonths),
          }
        : null,
    };

    try {
      await save.mutateAsync({ dealId: deal.id, data: payload });
      onDone();
    } catch (e: any) {
      const message = e?.response?.data?.message;
      setError(Array.isArray(message) ? message.join(', ') : message || 'Could not save the schedule.');
    }
  };

  const handleRemove = async () => {
    setError(null);
    try {
      // An empty save clears the schedule server-side.
      await save.mutateAsync({ dealId: deal.id, data: { installments: [], recurring: null } });
      onDone();
    } catch {
      setError('Could not remove the schedule.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <TypeToggle active={fixedOn} onClick={() => setFixedOn((v) => !v)} label="Fixed installments" />
        <TypeToggle active={recurringOn} onClick={() => setRecurringOn((v) => !v)} label="Recurring retainer" />
      </div>

      {fixedOn && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold text-grey-500 uppercase tracking-wider">
              Fixed installments
            </p>
            <div className="flex items-center gap-1">
              <button type="button" className="text-xs text-grey-500 hover:text-primary px-2 py-1 rounded hover:bg-grey-50" onClick={() => applyEvenSplit(2)}>
                50 / 50
              </button>
              <button type="button" className="text-xs text-grey-500 hover:text-primary px-2 py-1 rounded hover:bg-grey-50" onClick={() => applyEvenSplit(3)}>
                Thirds
              </button>
              <button type="button" className="text-xs text-grey-500 hover:text-primary px-2 py-1 rounded hover:bg-grey-50" onClick={() => applyEvenSplit(4)}>
                Quarters
              </button>
            </div>
          </div>

          {rows.map((row, index) => (
            <div key={row.key} className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                className="input w-[9.5rem]"
                value={row.dueDate}
                onChange={(e) =>
                  setRows((prev) => prev.map((r, i) => (i === index ? { ...r, dueDate: e.target.value } : r)))
                }
              />
              <div className="flex items-center">
                <button
                  type="button"
                  aria-label={row.mode === 'AMOUNT' ? 'Switch to percentage of deal value' : 'Switch to flat amount'}
                  className="px-2.5 py-2 text-sm font-semibold border border-grey-300 rounded-l-lg bg-grey-50 text-grey-600 hover:bg-grey-100"
                  onClick={() =>
                    setRows((prev) =>
                      prev.map((r, i) =>
                        i === index ? { ...r, mode: r.mode === 'AMOUNT' ? 'PERCENT' : 'AMOUNT' } : r,
                      ),
                    )
                  }
                >
                  {row.mode === 'AMOUNT' ? '$' : '%'}
                </button>
                <input
                  type="number"
                  min="0"
                  step={row.mode === 'PERCENT' ? '0.01' : '1'}
                  className="input rounded-l-none w-28"
                  placeholder={row.mode === 'PERCENT' ? '50' : '0'}
                  value={row.value}
                  onChange={(e) =>
                    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, value: e.target.value } : r)))
                  }
                />
              </div>
              <input
                type="text"
                className="input flex-1 min-w-[8rem]"
                placeholder="Label (e.g. On signing)"
                value={row.label}
                onChange={(e) =>
                  setRows((prev) => prev.map((r, i) => (i === index ? { ...r, label: e.target.value } : r)))
                }
              />
              <span className="text-sm text-grey-500 w-24 text-right tabular-nums">
                {row.mode === 'PERCENT' && dealValue !== null
                  ? formatCurrency(rowAmount(row), currency)
                  : ''}
              </span>
              <button
                type="button"
                className="p-2 rounded hover:bg-grey-100 text-grey-400 hover:text-error"
                onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                aria-label="Remove installment"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}

          <Button variant="ghost" size="sm" onClick={() => addRow()}>
            <Plus className="w-3.5 h-3.5" />
            Add installment
          </Button>
        </div>
      )}

      {recurringOn && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-grey-500 uppercase tracking-wider">
            Recurring retainer
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <Input
              label="Amount per period"
              type="number"
              min="0"
              step="1"
              placeholder="2000"
              value={recurringAmount}
              onChange={(e) => setRecurringAmount(e.target.value)}
            />
            <Select
              label="Cadence"
              value={cadence}
              onChange={(e) => setCadence(e.target.value as PaymentCadence)}
              options={cadenceOptions.map((c) => ({ value: c.value, label: c.label }))}
            />
            <Input
              label="Starts"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <Input
              label="Minimum months"
              type="number"
              min="1"
              step="1"
              value={minimumMonths}
              onChange={(e) => setMinimumMonths(e.target.value)}
            />
          </div>
          {toNumber(recurringAmount) > 0 && (
            <p className="text-xs text-grey-500">
              {occurrences} charge{occurrences === 1 ? '' : 's'} over the minimum term ={' '}
              <span className="font-semibold text-secondary">
                {formatCurrency(recurringTotal, currency)}
              </span>
            </p>
          )}
        </div>
      )}

      {error && <p className="text-sm text-error">{error}</p>}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-grey-100">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-grey-500">
            Scheduled{' '}
            <span className="font-semibold text-secondary">
              {formatCurrency(scheduledTotal, currency)}
            </span>
          </span>
          {dealValue === null ? (
            <span className="text-xs text-grey-400">No deal value to reconcile against</span>
          ) : (
            <span
              className={cn(
                'text-sm',
                remaining === 0 ? 'text-success' : remaining! > 0 ? 'text-warning' : 'text-error',
              )}
            >
              {remaining === 0
                ? 'Balanced'
                : remaining! > 0
                  ? `${formatCurrency(remaining!, currency)} remaining`
                  : `${formatCurrency(Math.abs(remaining!), currency)} over`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {deal.paymentSchedule && (
            <Button variant="ghost" size="sm" onClick={handleRemove} loading={save.isPending}>
              <Trash2 className="w-3.5 h-3.5" />
              Remove
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onDone}>Cancel</Button>
          <Button size="sm" onClick={handleSave} loading={save.isPending}>Save schedule</Button>
        </div>
      </div>
    </div>
  );
}

function TypeToggle({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
        active
          ? 'bg-primary/10 border-primary text-primary'
          : 'bg-white border-grey-300 text-grey-500 hover:border-grey-400',
      )}
    >
      {label}
    </button>
  );
}
